import imageCompression from 'browser-image-compression';
import { eachDayOfInterval, format, subDays } from 'date-fns';
import {
  DRAWER_WIDTH, BORDER_WIDTH_OPTIONS, BORDER_RADIUS_OPTIONS,
  GRADIENT_DIRECTIONS, THEME_DEFAULT_COLOR, ACCOUNT_GRACE_PERIOD_DAYS,
} from 'utils/constants';

export const getDaysLeft = (deletedAt) =>
  Math.max(0, Math.ceil(
    (new Date(deletedAt).getTime() + ACCOUNT_GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000 - Date.now()) / (24 * 60 * 60 * 1000)
  ));

export const isAnon = (user) => {
        return !user
    }
export const isValidateTitle = (title) => {
        return !title || title.length <= 50
    }
export const isValidateDesc = (bio) => {
        return !bio || bio.length <= 200
    }
export const compressAvatar = async (file) => {
    const sizeMB = file.size / 1024 / 1024;

    if (sizeMB > 10) throw new Error("too_big");

    const isHeic =
        file.type.includes("heic") ||
        file.type.includes("heif") ||
        file.name.toLowerCase().endsWith(".heic");

    try {
        if (sizeMB > 3) return { mode: "backend", file };
        if (isHeic) return { mode: "backend", file };

        let workingFile = file;

        const compressed = await imageCompression(workingFile, {
            maxSizeMB: 0.4,
            maxWidthOrHeight: 512,
            fileType: "image/webp",
            initialQuality: 0.65,
            useWebWorker: true,
        });

        return { mode: "front", file: compressed };
    } catch (error) {
        throw new Error("compress_failed");
    }
}
export const getAvatarUrl = (userImage) => {
    return `${process.env.REACT_APP_IMAGEKIT_URL}/avatars/${userImage.fileName}?v=${new Date(
        userImage.updatedAt
        ).getTime()}`
}
export const getInitials = (title, username) => {
    const source = (title?.trim() || username?.trim() || '').replace(/^@/, '');
    if (!source) return '?';
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
}
export const aggregateByLink = (analytics) => {
  const map = {};
  for (const entry of analytics) {
    const id = entry.link;
    if (!map[id]) map[id] = { linkId: id, clicks: 0 };
    map[id].clicks += entry.clicks;
  }
  return Object.values(map).sort((a, b) => b.clicks - a.clicks);
};

export const buildDailyData = (visits, days) => {
  const today = new Date();
  const range = eachDayOfInterval({ start: subDays(today, days - 1), end: today });
  const map = Object.fromEntries(visits.map(v => [v.date, v.visits]));
  return range.map(date => {
    const key = format(date, 'yyyy-MM-dd');
    return { date: key, label: format(date, 'dd/MM'), visits: map[key] ?? 0 };
  });
};

export const openedMixin = (theme) => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shorter,
  }),
  overflowY: 'hidden',
});
export const buildCurvePath = (pts, tension = 0.4) => {
    if (pts.length < 2) return '';
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
        const prev = pts[i - 1];
        const curr = pts[i];
        const dx = curr.x - prev.x;
        d += ` C ${prev.x + dx * tension} ${prev.y}, ${curr.x - dx * tension} ${curr.y}, ${curr.x} ${curr.y}`;
    }
    return d;
};

export const getBackground = (bg) => {
  if (!bg) return undefined;
  if (bg.type === 'solid') return bg.solid;
  const { left, right, direction } = bg.gradient || {};
  if (!left || !right) return undefined;
  return `linear-gradient(${direction || 'to right'}, ${left}, ${right})`;
};

export const getPresetBg = (themeData) => {
  if (themeData.background.type === 'solid') return themeData.background.solid;
  const { left, right, direction } = themeData.background.gradient;
  return `linear-gradient(${direction}, ${left}, ${right})`;
};

export const mapThemeToForm = (theme) => ({
  textPrimary:    theme.text?.primary || THEME_DEFAULT_COLOR,
  textSecondary:  theme.text?.secondary || THEME_DEFAULT_COLOR,
  btnText:        theme.button?.text || THEME_DEFAULT_COLOR,
  btnBg:          theme.button?.background || THEME_DEFAULT_COLOR,
  btnBorderColor: theme.button?.border?.color || THEME_DEFAULT_COLOR,
  btnShadowColor: theme.button?.shadow?.color || THEME_DEFAULT_COLOR,
  bgSolid:        theme.background?.solid || THEME_DEFAULT_COLOR,
  bgLeft:         theme.background?.gradient?.left || THEME_DEFAULT_COLOR,
  bgRight:        theme.background?.gradient?.right || THEME_DEFAULT_COLOR,
  borderWidth:    theme.button?.border?.width
    ? BORDER_WIDTH_OPTIONS.find(o => o.label === theme.button.border.width) ?? null
    : null,
  borderRadius:   theme.button?.border?.radius
    ? BORDER_RADIUS_OPTIONS.find(o => o.label === theme.button.border.radius) ?? BORDER_RADIUS_OPTIONS[5]
    : BORDER_RADIUS_OPTIONS[5],
  shadowEnabled:  theme.button?.shadow?.enabled || false,
  bgType:         theme.background?.type || 'gradient',
  gradientDir:    GRADIENT_DIRECTIONS.find(d => d.value === theme.background?.gradient?.direction)?.value ?? GRADIENT_DIRECTIONS[0].value,
});

/**
 * Parses an `rgb(R,G,B)` or `rgba(R,G,B,A)` string and returns its perceived
 * luminance on the 0–255 scale (Rec. 709 weights).
 * Returns 128 (mid-gray) when the string can't be parsed — safe default.
 */
export const getRgbLuminance = (color) => {
  if (typeof color !== 'string') return 128;
  const rgbMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }
  const hexMatch = color.match(/^#([a-f\d]{6}|[a-f\d]{3})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }
  return 128;
};

/**
 * Returns the WCAG-ish contrast ratio between two colors (1.0 = identical,
 * 21.0 = max contrast like black vs white). Below ~3.0 = visually weak.
 * Uses normalized luminance + the standard (L_light + 0.05) / (L_dark + 0.05).
 */
export const getContrastRatio = (color1, color2) => {
  const l1 = getRgbLuminance(color1) / 255;
  const l2 = getRgbLuminance(color2) / 255;
  const lighter = Math.max(l1, l2);
  const darker  = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Returns true when `color` has enough perceived luminance to remain visible
 * on top of a dark surface (the FAB glass + the drawer background).
 * Use this to decide whether to honor the user's identity color or fall back
 * to a semantic default that's guaranteed readable.
 */
export const isReadableOnDark = (color, threshold = 60) =>
  getRgbLuminance(color) >= threshold;

/**
 * Returns the text color (`#000` or `#fff`) that's most readable on top of
 * the given background color. Useful for surfaces painted with the user's
 * identity color: when the theme is bright (light pinks, neon greens) we need
 * dark text; when it's mid-dark we keep white.
 *
 * Threshold 140 picks the lighter side of the WCAG contrast curve — a color
 * brighter than that is closer to white, so black wins.
 */
export const getReadableTextOn = (backgroundColor) =>
  getRgbLuminance(backgroundColor) > 140 ? '#000' : '#fff';

export const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shorter,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(6)})`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(7)})`,
  },
});